/**
 * AppConfigPage — admin App Config management (UI for the shipped STORY-008-004
 * router). An AdminTable of hot-reloadable key/value rows + a BottomSheet edit
 * dialog whose VALUE input adapts to the selected `value_type`. Saving PUTs
 * `/admin/config/{key}`; a 422 `app_config.invalid_value` (server validates
 * value vs value_type BEFORE persisting) or `app_config.value_out_of_range`
 * (server validates value vs a registered bounds registry BEFORE persisting
 * — bug-wave 2026-09-10) surfaces inline, naming the accepted range. A row
 * whose STORED value falls outside its bounds (e.g. written before the
 * bound existed, or directly to the DB) is flagged with a quiet warning
 * pill (`in_effect: false` from the API) rather than looking identical to
 * an applied value. Read gated upstream by RequirePermissionRoute
 * (ops.app_config.read); the Save affordance is gated by
 * ops.app_config.write (server RBAC is the real enforcement).
 *
 * The Guest access card (GuestChatConfigForm, SCRUM-273) sits above the
 * table.
 */
import { useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminTable, type AdminTableColumn } from "@/features/admin/components/AdminTable";
import { GuestChatConfigForm } from "@/features/admin/app-config/GuestChatConfigForm";
import { useRequirePermission } from "@/features/admin/hooks/useRequirePermission";
import { ApiError } from "@/lib/api-client";
import {
  CONFIG_VALUE_TYPES,
  type AppConfigBounds,
  type AppConfigItem,
  type ConfigValueType,
} from "@/features/admin/app-config/api";
import { useConfigList, usePutConfig } from "@/features/admin/app-config/hooks";

const FIELD_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

/**
 * Extracts `min`/`max`/`value` from an `app_config.value_out_of_range`
 * problem body (RFC 9457 extension members set by
 * `garden.admin.config_router.ConfigValueOutOfRangeError`). Returns `null`
 * if any field is missing/mistyped so the caller can fall back to a
 * generic message rather than rendering `undefined`.
 */
function outOfRangeDetails(
  problem: Record<string, unknown>,
): { min: number; max: number; value: number } | null {
  const { min, max, value } = problem;
  if (typeof min === "number" && typeof max === "number" && typeof value === "number") {
    return { min, max, value };
  }
  return null;
}

interface EditState {
  key: string;
  value: string;
  valueType: ConfigValueType;
  description: string;
  /** Whether the STORED value (as loaded) is currently in effect — see
   * `AppConfigItem.in_effect`. Drives the "not applied" banner in the
   * editor; NOT re-evaluated client-side as the admin types a new value. */
  inEffect: boolean;
  bounds: AppConfigBounds | null;
}

export function AppConfigPage(): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const canWrite = useRequirePermission("ops.app_config.write");
  const { data, isLoading, isError } = useConfigList();
  const putConfig = usePutConfig();

  const [edit, setEdit] = useState<EditState | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const items: AppConfigItem[] = data?.items ?? [];

  const openEditor = (row: AppConfigItem): void => {
    setFieldError(null);
    setEdit({
      key: row.key,
      value: row.value,
      valueType: (CONFIG_VALUE_TYPES as readonly string[]).includes(row.value_type)
        ? (row.value_type as ConfigValueType)
        : "string",
      description: row.description ?? "",
      inEffect: row.in_effect,
      bounds: row.bounds,
    });
  };

  const closeEditor = (): void => {
    setEdit(null);
    setFieldError(null);
  };

  const fmtDate = (iso: string): string => new Date(iso).toLocaleString(i18n.language);

  /** Hint text for the "not in effect" warning pill/banner — names the
   * accepted range when known, else a generic fallback. */
  const notInEffectHint = (bounds: AppConfigBounds | null): string =>
    bounds !== null
      ? t("appConfig.notInEffect.hint", { min: bounds.minimum, max: bounds.maximum })
      : t("appConfig.notInEffect.hintGeneric");

  const handleSave = (): void => {
    if (edit === null) return;
    setFieldError(null);
    putConfig.mutate(
      {
        key: edit.key,
        body: {
          value: edit.value,
          value_type: edit.valueType,
          description: edit.description.trim() === "" ? null : edit.description,
        },
      },
      {
        onSuccess: () => {
          toast.success(t("appConfig.saveSuccess"));
          closeEditor();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === "app_config.invalid_value") {
            setFieldError(t("appConfig.error.invalidValue"));
            return;
          }
          if (err instanceof ApiError && err.code === "app_config.value_out_of_range") {
            const details = outOfRangeDetails(err.problem);
            setFieldError(
              details !== null
                ? t("appConfig.error.outOfRange", details)
                : t("appConfig.error.outOfRangeGeneric"),
            );
            return;
          }
          toast.error(t("error.generic"));
        },
      },
    );
  };

  const columns: readonly AdminTableColumn[] = [
    { key: "key", label: t("appConfig.columns.key") },
    { key: "value", label: t("appConfig.columns.value") },
    { key: "type", label: t("appConfig.columns.type") },
    { key: "updated", label: t("appConfig.columns.updated") },
  ];

  const renderCell = (row: AppConfigItem, key: string): ReactNode => {
    switch (key) {
      case "key":
        return <span className="font-mono text-sm">{row.key}</span>;
      case "value":
        return (
          <span className="flex items-center gap-2">
            <span className="block max-w-[28ch] truncate text-[var(--g-secondary)]">
              {row.value}
            </span>
            {!row.in_effect && (
              <span title={notInEffectHint(row.bounds)}>
                <GPill tone="warn" size="sm" leading="warn">
                  {t("appConfig.notInEffect.badge")}
                </GPill>
              </span>
            )}
          </span>
        );
      case "type":
        return (
          <GPill tone="neutral" size="sm">
            {t(`appConfig.type.${row.value_type}`, { defaultValue: row.value_type })}
          </GPill>
        );
      case "updated":
        return <span className="tabular-nums">{fmtDate(row.updated_at)}</span>;
      default:
        return "";
    }
  };

  const renderMobileCard = (row: AppConfigItem): ReactNode => (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-sm">{row.key}</span>
      <span className="flex items-center gap-2">
        <span className="truncate text-sm text-[var(--g-secondary)]">{row.value}</span>
        {!row.in_effect && (
          <span title={notInEffectHint(row.bounds)}>
            <GPill tone="warn" size="sm" leading="warn">
              {t("appConfig.notInEffect.badge")}
            </GPill>
          </span>
        )}
      </span>
      <span className="text-xs text-[var(--g-secondary)]">{fmtDate(row.updated_at)}</span>
    </div>
  );

  const renderValueField = (): ReactElement => {
    if (edit === null) return <></>;
    if (edit.valueType === "bool") {
      return (
        <select
          data-testid="app-config-value-input"
          className={FIELD_CLASS}
          value={edit.value}
          disabled={!canWrite}
          onChange={(e) => setEdit({ ...edit, value: e.target.value })}
        >
          <option value="true">{t("appConfig.edit.boolTrue")}</option>
          <option value="false">{t("appConfig.edit.boolFalse")}</option>
        </select>
      );
    }
    if (edit.valueType === "json") {
      return (
        <textarea
          data-testid="app-config-value-input"
          className={`${FIELD_CLASS} min-h-[120px] font-mono`}
          value={edit.value}
          disabled={!canWrite}
          onChange={(e) => setEdit({ ...edit, value: e.target.value })}
        />
      );
    }
    const numeric = edit.valueType === "int" || edit.valueType === "float";
    return (
      <input
        data-testid="app-config-value-input"
        type={numeric ? "number" : "text"}
        className={FIELD_CLASS}
        value={edit.value}
        disabled={!canWrite}
        onChange={(e) => setEdit({ ...edit, value: e.target.value })}
      />
    );
  };

  return (
    <section data-testid="app-config-page" className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-[var(--g-primary-ink)]">
          {t("appConfig.title")}
        </h1>
        <p className="text-sm text-[var(--g-secondary)]">{t("appConfig.subtitle")}</p>
      </header>

      {/* SCRUM-273 — guest chat's switches and limits, purpose-built at the
          top (spec); the generic table below still lists every key. */}
      <GuestChatConfigForm />

      <AdminTable<AppConfigItem>
        columns={columns}
        rows={items}
        rowKey={(r) => r.key}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        onRowActivate={openEditor}
        rowAriaLabel={(r) => t("appConfig.edit.open", { key: r.key })}
        testid="app-config-table"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("appConfig.empty")}
        errorLabel={t("appConfig.errorLoading")}
      />

      <BottomSheet open={edit !== null} onClose={closeEditor} title={edit?.key ?? ""}>
        {edit !== null && (
          <div data-testid="app-config-dialog" className="flex flex-col gap-4">
            {!edit.inEffect && (
              <p
                data-testid="app-config-not-in-effect-banner"
                className="rounded-[var(--g-radius-sm)] bg-[var(--g-warn)]/10 px-3 py-2 text-sm text-[var(--g-warn)]"
              >
                {notInEffectHint(edit.bounds)}
              </p>
            )}

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">{t("appConfig.edit.typeLabel")}</span>
              <select
                data-testid="app-config-type-select"
                className={FIELD_CLASS}
                value={edit.valueType}
                disabled={!canWrite}
                onChange={(e) =>
                  setEdit({ ...edit, valueType: e.target.value as ConfigValueType })
                }
              >
                {CONFIG_VALUE_TYPES.map((vt) => (
                  <option key={vt} value={vt}>
                    {t(`appConfig.type.${vt}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">{t("appConfig.edit.valueLabel")}</span>
              {renderValueField()}
              {edit.bounds !== null && (
                <span
                  data-testid="app-config-bounds-hint"
                  className="text-xs text-[var(--g-secondary)]"
                >
                  {t("appConfig.edit.boundsHint", {
                    min: edit.bounds.minimum,
                    max: edit.bounds.maximum,
                  })}
                </span>
              )}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">
                {t("appConfig.edit.descriptionLabel")}
              </span>
              <input
                className={FIELD_CLASS}
                value={edit.description}
                disabled={!canWrite}
                onChange={(e) => setEdit({ ...edit, description: e.target.value })}
              />
            </label>

            {fieldError !== null && (
              <p
                data-testid="app-config-error"
                role="alert"
                className="text-sm text-[var(--g-error)]"
              >
                {fieldError}
              </p>
            )}

            <p className="text-xs text-[var(--g-secondary)]">
              {t("appConfig.edit.historyHint")}{" "}
              <a className="underline" href="/admin/audit-log">
                {t("appConfig.edit.historyLink")}
              </a>
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={closeEditor}>
                {t("appConfig.edit.cancel")}
              </Button>
              <Button
                data-testid="app-config-save"
                disabled={!canWrite || putConfig.isPending}
                onClick={handleSave}
              >
                {t("appConfig.edit.save")}
              </Button>
            </div>

            {!canWrite && (
              <p className="text-xs text-[var(--g-secondary)]">
                {t("appConfig.readOnlyNote")}
              </p>
            )}
          </div>
        )}
      </BottomSheet>
    </section>
  );
}

export default AppConfigPage;
